import { useState, useEffect, useCallback } from 'react';
import { cityService } from '../services';
import { City } from '../types/city.types';
import { toast } from 'react-toastify';

export const useCities = () => {
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await cityService.getAllCities();
      const fetchedCities = response.data?.cities || [];
      setCities(fetchedCities);
    } catch (err: any) {
      console.error('❌ Error fetching cities:', err);
      const errorMessage = err?.response?.data?.message || 'Failed to fetch cities';
      setError(errorMessage);
      toast.error(errorMessage);
      setCities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  const createCity = useCallback(async (data: { name: string; isDefault?: boolean }) => {
    try {
      await cityService.createCity(data);
      toast.success('City created successfully');
      await fetchCities();
    } catch (err: any) {
      console.error('❌ Error creating city:', err);
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || 'Failed to create city';
      toast.error(errorMessage);
      throw err;
    }
  }, [fetchCities]);

  const updateCity = useCallback(async (cityId: string, data: { name?: string; isDefault?: boolean }) => {
    try {
      await cityService.updateCity(cityId, data);
      toast.success('City updated successfully');
      await fetchCities();
    } catch (err: any) {
      console.error('❌ Error updating city:', err);
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || 'Failed to update city';
      toast.error(errorMessage);
      throw err;
    }
  }, [fetchCities]);

  const deleteCity = useCallback(async (cityId: string) => {
    try {
      await cityService.deleteCity(cityId);
      toast.success('City deleted successfully');
      setCities((prev) => prev.filter((city) => city.cityId !== cityId));
    } catch (err: any) {
      console.error('❌ Error deleting city:', err);
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || 'Failed to delete city';
      toast.error(errorMessage);
      throw err;
    }
  }, []);

  const reorderCities = useCallback(async (reorderedCities: City[]) => {
    try {
      const reorderData = reorderedCities.map((city, index) => ({
        cityId: city.cityId,
        displayOrder: index + 1,
      }));

      await cityService.reorderCities({ cities: reorderData });
      toast.success('Cities reordered successfully');
      setCities(reorderedCities);
    } catch (err: any) {
      console.error('❌ Error reordering cities:', err);
      const errorMessage = err?.response?.data?.message || 'Failed to reorder cities';
      toast.error(errorMessage);
      // Revert to the persisted order
      await fetchCities();
    }
  }, [fetchCities]);

  return {
    cities,
    loading,
    error,
    refetch: fetchCities,
    createCity,
    updateCity,
    deleteCity,
    reorderCities,
  };
};
